import { useState, useRef, useCallback, useEffect } from "react";
import { getDefaultFormValues } from "../config/defaultValues";
import {
  loadDemoInput,
  saveDemoInput,
  clearDemoInput,
} from "../utils/demoStorage";
import { mergeDemoInput } from "../utils/mergeDemoInput";
import { createDemoDraftTracker } from "../utils/demoDraftTracker";

// デモ（TOPページ埋め込み）の入力内容を localStorage に自動保存 / 復元する。
// 保存・読み込みの失敗は demoStorageWarning（"save" | "load" | null）で通知する。
export function useDemoDraft({ demo, methods, watchedValues, onRestore }) {
  const restoredRef = useRef(false);
  const [draftTracker] = useState(() => createDemoDraftTracker(saveDemoInput));
  const [demoStorageWarning, setDemoStorageWarning] = useState(null);
  // 保存失敗の警告は1画面表示につき1回だけ
  const saveWarnedRef = useRef(false);
  // 読み込み失敗中（＝壊れたデータが残ったまま）は、保存失敗の警告で上書きしない
  const loadFailedRef = useRef(false);

  const dismissDemoStorageWarning = useCallback(() => {
    loadFailedRef.current = false;
    setDemoStorageWarning(null);
  }, []);

  // 保存の成否を警告に反映する。成功で警告を消し、失敗時は1回だけ警告を出す。
  // 保存できたかどうかを返す（本登録への引き継ぎ可否の判断に使う）
  const handleSaveResult = useCallback((saved) => {
    if (saved) {
      // 読み込み失敗の警告は「入力をリセット」または閉じるまで残す（9.4）。
      // 保存の成功だけで自動的に消すと、Step移動のたびに警告が消えてしまう
      if (!loadFailedRef.current) setDemoStorageWarning(null);
      return true;
    }
    // 読み込み失敗の案内（壊れたデータのリセットを促す）を、保存失敗の案内で消さない
    if (loadFailedRef.current) return false;
    if (!saveWarnedRef.current) {
      saveWarnedRef.current = true;
      setDemoStorageWarning("save");
    }
    return false;
  }, []);

  // 現在の入力を、差分に関係なく即時保存する
  const flushDemoDraft = useCallback(() => {
    // demo=false では保存するものが無いので、引き継ぎを妨げる失敗も無い
    if (!demo) return true;
    return handleSaveResult(draftTracker.saveNow(methods.getValues()));
  }, [demo, methods, draftTracker, handleSaveResult]);

  // 初回だけ localStorage → フォームへ復元
  useEffect(() => {
    if (!demo || restoredRef.current) return;
    restoredRef.current = true;

    const { values, failed } = loadDemoInput();
    if (failed) {
      // 読み込み失敗時は復元せず、Step 1 のまま通知する
      loadFailedRef.current = true;
      setDemoStorageWarning("load");
    } else if (values) {
      // 項目のズレは復元時に寛容に扱う
      methods.reset(mergeDemoInput(values, getDefaultFormValues()));
      // 復元しただけなので、この内容を「まだ何も編集していない状態」として扱ってもらう
      onRestore?.();
    }
    // 開いただけで保存しないよう、この時点の内容を保存済みとして記録する
    draftTracker.markSaved(methods.getValues());
  }, [demo, methods, onRestore, draftTracker]);

  // 入力が変わったら 500ms 後に保存（3.3）。最後に保存した内容と同じならスキップ（3.4）
  useEffect(() => {
    if (!demo) return;
    const id = setTimeout(() => {
      const saved = draftTracker.saveIfChanged(methods.getValues());
      if (saved !== null) handleSaveResult(saved);
    }, 500);
    return () => clearTimeout(id);
  }, [demo, methods, watchedValues, draftTracker, handleSaveResult]);

  // 入力内容をすべて削除する
  const clearDemoForm = useCallback(() => {
    clearDemoInput();
    methods.reset(getDefaultFormValues());
    // 削除直後に自動保存で書き戻さないよう、空の状態を保存済みとして記録する
    draftTracker.markSaved(methods.getValues());
    loadFailedRef.current = false;
    setDemoStorageWarning(null);
  }, [methods, draftTracker]);

  return {
    flushDemoDraft,
    clearDemoForm,
    demoStorageWarning,
    dismissDemoStorageWarning,
  };
}
